import useSSE from '../useSSE';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import api from '../utils/api';

const STATUS_COLORS = {
  not_started:'#8A93A6', planning:'#2563EB', in_progress:'#5B5BD6',
  escalated:'#DC2626', at_risk:'#D97706', on_hold:'#8A93A6',
  completed:'#16A34A', cancelled:'#6B7280'
};
const STATUS_LABELS = {
  not_started:'Not Started', planning:'Planning', in_progress:'In Progress',
  escalated:'Escalated', at_risk:'At Risk', on_hold:'On Hold',
  completed:'Completed', cancelled:'Cancelled'
};

function pColor(p) { return p>=100?'#16A34A':p>=60?'#5B5BD6':p>=30?'#D97706':'#DC2626'; }

function Avatar({name,color,size=28}) {
  const i=(name||'?').split(' ').map(w=>w[0]).slice(0,2).join('').toUpperCase();
  return <div style={{width:size,height:size,borderRadius:'50%',background:color||'#5B5BD6',display:'flex',alignItems:'center',justifyContent:'center',fontSize:size*.34,fontWeight:'700',color:'#fff',flexShrink:0}}>{i}</div>;
}

// ── Weight Adjust Modal for Lists (mirrors DashboardPage WeightAdjustModal) ────
function ListWeightAdjModal({ newItemName, siblings, usedWeight, remaining, onConfirm, onCancel }) {
  const [sibs, setSibs] = React.useState(() => siblings.map(s => ({ ...s })));
  const [newWeight, setNewWeight] = React.useState(() => {
    const suggested = Math.max(1, Math.floor(100 / (siblings.length + 1)));
    return Math.min(remaining > 0 ? remaining : suggested, suggested);
  });

  function distributeProportionalForWeight(nw) {
    const target = 100 - nw;
    if (usedWeight === 0) {
      const each = Math.floor(target / siblings.length);
      setSibs(siblings.map((s,i) => ({ ...s, weight: i===siblings.length-1 ? target-each*(siblings.length-1) : each })));
      return;
    }
    const adjusted = siblings.map(s => ({ ...s, weight: Math.max(0, Math.round((s.weight / usedWeight) * target)) }));
    const diff = target - adjusted.reduce((s,x) => s+x.weight, 0);
    if (adjusted.length > 0) adjusted[0].weight = Math.max(0, adjusted[0].weight + diff);
    setSibs(adjusted);
  }

  React.useEffect(() => { distributeProportionalForWeight(newWeight); }, []); // eslint-disable-line

  const sibTotal  = sibs.reduce((s,x) => s+(x.weight||0), 0);
  const grandTotal = sibTotal + newWeight;
  const isValid   = grandTotal <= 100 && newWeight > 0;

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ width:'540px' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'12px', marginBottom:'20px', paddingBottom:'16px', borderBottom:'1px solid var(--border)' }}>
          <div style={{ width:'40px', height:'40px', borderRadius:'10px', background:'rgba(91,91,214,0.1)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'20px' }}>⚖️</div>
          <div>
            <h3 style={{ fontSize:'16px', fontWeight:'700', margin:0 }}>Weight Distribution</h3>
            <p style={{ fontSize:'12px', color:'var(--muted)', margin:'2px 0 0' }}>Adding <strong>"{newItemName}"</strong> — adjust weights so they sum correctly</p>
          </div>
        </div>
        <div style={{ background:'rgba(91,91,214,0.06)', border:'1px solid rgba(91,91,214,0.2)', borderRadius:'8px', padding:'10px 14px', marginBottom:'16px', fontSize:'12px', color:'var(--text)' }}>
          Existing lists use <strong>{usedWeight}%</strong>. Remaining: <strong>{remaining}%</strong>. Adjust so total ≤ 100%.
        </div>
        <div className="form-group">
          <label className="form-label">New list weight: <strong style={{ color:'var(--brand)' }}>"{newItemName}"</strong></label>
          <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
            <input type="range" min="1" max="100" value={newWeight}
              onChange={e => { const nw=parseInt(e.target.value); setNewWeight(nw); distributeProportionalForWeight(nw); }}
              style={{ flex:1, accentColor:'var(--brand)' }} />
            <input type="number" min="1" max="100" value={newWeight}
              onChange={e => setNewWeight(Math.min(100, Math.max(1, parseInt(e.target.value)||1)))}
              className="input" style={{ width:'65px', textAlign:'center', fontWeight:'700' }} />
          </div>
        </div>
        <div style={{ display:'flex', gap:'8px', marginBottom:'14px' }}>
          <button className="btn btn-sm" onClick={()=>distributeProportionalForWeight(newWeight)}>⚖️ Proportional</button>
          <button className="btn btn-sm" onClick={()=>{
            const target=100-newWeight; const each=Math.floor(target/siblings.length);
            setSibs(siblings.map((s,i)=>({...s,weight:i===siblings.length-1?target-each*(siblings.length-1):each})));
          }}>═ Equal</button>
          <div style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:'6px', fontSize:'12px', color: isValid?'#16A34A':'#DC2626', fontWeight:'700' }}>
            Total: {grandTotal}% {isValid ? '✓' : '— over 100%'}
          </div>
        </div>
        <div style={{ marginBottom:'20px', maxHeight:'200px', overflowY:'auto' }}>
          {sibs.map(s => (
            <div key={s.id} style={{ display:'flex', alignItems:'center', gap:'10px', padding:'8px 0', borderBottom:'1px solid var(--border)' }}>
              <span style={{ flex:1, fontSize:'13px', color:'var(--text)', fontWeight:'500' }}>{s.name}</span>
              <span style={{ fontSize:'11px', color:'var(--muted)', minWidth:'50px', textAlign:'right' }}>was {s.weight||0}%</span>
              <input type="number" min="0" max="100" value={s.weight}
                onChange={e => setSibs(prev => prev.map(x => x.id===s.id ? {...x, weight:Math.max(0,Math.min(100,parseInt(e.target.value)||0))} : x))}
                className="input" style={{ width:'65px', textAlign:'center', fontWeight:'700', borderColor: isValid?'var(--border)':'#DC2626' }} />
              <span style={{ fontSize:'11px', color:'var(--muted)' }}>%</span>
            </div>
          ))}
        </div>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <span style={{ fontSize:'12px', fontWeight:'700', color: isValid?'#16A34A':'#DC2626' }}>
            Total: {grandTotal}%{isValid?' ✓':' — reduce more'}
          </span>
          <div style={{ display:'flex', gap:'8px' }}>
            <button className="btn" onClick={onCancel}>Cancel</button>
            <button className="btn btn-primary" onClick={() => onConfirm(newWeight, sibs)} disabled={!isValid}>
              Confirm & Create
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function PhaseDashboard() {
  const { phaseId } = useParams();
  const navigate = useNavigate();
  const ctx = useOutletContext?.() || {};
  const [phase, setPhase] = useState(null);
  const [siblingPhases, setSiblingPhases] = useState([]);
  const [toast, setToast] = useState(null);
  const [weightModal, setWeightModal] = useState(null);

  function showToast(msg, type='error') {
    setToast({msg,type});
    setTimeout(()=>setToast(null),5000);
  }
  const [milestones, setMilestones] = useState([]);
  const [allTasks, setAllTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const silentRef = React.useRef(false); // true during silent poll refresh

  // Real-time SSE — use projectId from loaded phase data
  const [_sseProjectId, _setSseProjectId] = React.useState(null);
  React.useEffect(()=>{ if(milestones?.[0]?.project_id) _setSseProjectId(milestones[0].project_id); },[milestones]);
  useSSE(_sseProjectId, {
    task_updated:  () => { silentRef.current=true; load().finally(()=>{ silentRef.current=false; }); },
    phase_updated: () => { silentRef.current=true; load().finally(()=>{ silentRef.current=false; }); },
    list_created:      () => { silentRef.current=true; load().finally(()=>{ silentRef.current=false; }); },
    list_updated:      () => { silentRef.current=true; load().finally(()=>{ silentRef.current=false; }); },
    timesheet_updated: () => { silentRef.current=true; load().finally(()=>{ silentRef.current=false; }); },
  });

  // Also reload when global access changes (phase made private/public)
  React.useEffect(() => {
    function onAccessChanged(evt) {
      if (!evt.detail?.projectId || evt.detail.projectId === _sseProjectId) {
        silentRef.current = true;
        load().finally(() => { silentRef.current = false; });
      }
    }
    window.addEventListener('trajectory:access_changed', onAccessChanged);
    return () => window.removeEventListener('trajectory:access_changed', onAccessChanged);
  }, [_sseProjectId]);
  const [editingPhaseName, setEditingPhaseName] = useState(false);
  const [phaseNameVal, setPhaseNameVal] = useState('');
  const [showNewList,       setShowNewList]       = useState(false);
  const [newListName,       setNewListName]       = useState('');
  const [newListColor,      setNewListColor]      = useState('#5B5BD6');
  const [addingList,        setAddingList]        = useState(false);
  const [listWeightModal,   setListWeightModal]   = useState(null);

  useEffect(()=>{ load(); },[phaseId]);
  useEffect(()=>{ if(phase?.name) setPhaseNameVal(phase.name); },[phase?.name]);

  // Silent background refresh — loads fresh data without showing spinner
  useEffect(()=>{
    let lastTs=null, active=true, fails=0;
    const poll=async()=>{
      if(!active||!phase?.project_id) return;
      try {
        const r=await api.get(`/spaces/${phase.project_id}/last-updated`).catch(()=>null);
        if(!r){fails++;if(fails>4)active=false;return;}
        fails=0;
        if(lastTs===null){lastTs=r.last_updated;return;}
        if(r.last_updated!==lastTs){
          lastTs=r.last_updated;
          // Silently reload tasks without setLoading(true)
          const mss=await api.get(`/folders/${phaseId}/lists`).catch(()=>null);
          if(mss&&active){
            setMilestones(Array.isArray(mss)?mss:[]);
            const taskArrays=await Promise.all((Array.isArray(mss)?mss:[]).map(ms=>
              api.get(`/lists/${ms.id}/tasks`).catch(()=>[])
            ));
            if(active) setAllTasks(taskArrays.flat());
          }
        }
      } catch(_){fails++;}
    };
    const iv=setInterval(poll, 8000);
    return()=>{active=false;clearInterval(iv);};
  },[phaseId,phase?.project_id]);

  async function savePhaseNameFn() {
    setEditingPhaseName(false);
    if (!phaseNameVal.trim() || phaseNameVal.trim()===phase?.name) return;
    try { await api.put('/phases/'+phaseId,{name:phaseNameVal.trim()}); load(); } catch(e) {}
  }



    async function createList() {
    if (!newListName.trim()) return;
    setAddingList(true);
    try {
      // Mirror DashboardPage weight logic exactly
      const usedWeight = milestones.reduce((s, ms) => s + (ms.weight || 0), 0);
      const remaining  = Math.max(0, 100 - usedWeight);
      if (milestones.length > 0 && remaining < 100) {
        // Existing lists have weight — show weight distribution modal
        setListWeightModal({
          newItemName: newListName.trim(),
          newItemColor: newListColor,
          siblings: milestones.map(ms => ({ id: ms.id, name: ms.name, weight: ms.weight || 0 })),
          usedWeight,
          remaining,
          onConfirm: async (newWeight, adjustedSiblings) => {
            setListWeightModal(null);
            for (const s of adjustedSiblings) {
              await api.put(`/lists/${s.id}`, { weight: s.weight }).catch(() => {});
            }
            await api.post(`/folders/${phaseId}/lists`, {
              name: newListName.trim(), color: newListColor, weight: newWeight,
            });
            setNewListName(''); setNewListColor('#5B5BD6'); setShowNewList(false);
            load();
            if (ctx?.reloadSidebar && phase?.project_id) ctx.reloadSidebar(phase.project_id);
          },
          onCancel: () => { setListWeightModal(null); },
        });
        setAddingList(false);
        return;
      }
      // No existing lists or all zero weight — create with remaining (or 100)
      await api.post(`/folders/${phaseId}/lists`, {
        name: newListName.trim(), color: newListColor, weight: remaining || 100,
      });
      setNewListName(''); setNewListColor('#5B5BD6'); setShowNewList(false);
      load();
      if (ctx?.reloadSidebar && phase?.project_id) ctx.reloadSidebar(phase.project_id);
    } catch(e) { console.error(e); }
    setAddingList(false);
  }

  async function load() {
    if(!silentRef.current) setLoading(true);
    try {
      // Load phase info (name, weight, visibility)
      const phaseInfo = await api.get(`/phases/${phaseId}`).catch(()=>null);
      if (phaseInfo) {
        setPhase(phaseInfo);
        // Load sibling phases for weight validation
        if (phaseInfo.project_id) {
          const siblings = await api.get(`/spaces/${phaseInfo.project_id}/folders`).catch(()=>[]);
          setSiblingPhases(Array.isArray(siblings)?siblings:[]);
        }
      }

      // Load milestones + dashboard stats (includes budgetHours and loggedHours per list)
      const [mss, dashData] = await Promise.all([
        api.get(`/folders/${phaseId}/lists`).catch(()=>[]),
        api.get(`/phases/${phaseId}/dashboard`).catch(()=>null),
      ]);
      // Merge loggedHours and budgetHours from dashboard into milestones
      const dashMilestones = dashData?.milestones||[];
      const msEnriched = (Array.isArray(mss)?mss:[]).map(ms=>{
        const dm = dashMilestones.find(d=>d.id===ms.id)||{};
        return { ...ms, budgetHours: dm.budgetHours||ms.budgetHours||0, loggedHours: dm.loggedHours||ms.loggedHours||0 };
      });
      setMilestones(msEnriched);

      // Load all tasks across milestones
      const taskArrays = await Promise.all(msEnriched.map(ms=>
        api.get(`/lists/${ms.id}/tasks`).catch(()=>[])
      ));
      setAllTasks(taskArrays.flat());
    } catch(e) {
      console.error(e);
      if (e?.status === 403 || e?.error?.includes('denied') || e?.error?.includes('private')) {
        setPageError('Access denied — this phase is private. Contact the project manager to request access.');
      }
    }
    setLoading(false);
  }

  if (loading) return (
    <div className="page-enter" style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',color:'var(--muted)',gap:'10px'}}>
      <div style={{width:16,height:16,border:'2px solid var(--border)',borderTopColor:'var(--brand)',borderRadius:'50%',animation:'spin .7s linear infinite'}}/>
      Loading phase dashboard…
    </div>
  );

  const topTasks = allTasks.filter(t=>!t.parent_task_id);
  const totalTasks = topTasks.length;
  const completedTasks = topTasks.filter(t=>t.status==='completed').length;
  const inProgressTasks = topTasks.filter(t=>t.status==='in_progress').length;
  const atRiskTasks = topTasks.filter(t=>['at_risk','escalated'].includes(t.status)).length;
  const overdueTasks = topTasks.filter(t=>t.due_date&&t.due_date<new Date().toISOString().slice(0,10)&&t.status!=='completed').length;
  const completionRate = totalTasks>0?Math.round((completedTasks/totalTasks)*100):0;

  // Phase progress — equal-weight fallback when no weights set
  const totalMsWeight = milestones.reduce((s, ms) => s + (ms.weight || 0), 0);
  let phaseContribution = 0;
  if (totalMsWeight === 0) {
    // Equal weight — average of all list progresses
    if (milestones.length > 0) {
      phaseContribution = milestones.reduce((s, ms) => s + (ms.progress || 0), 0) / milestones.length;
    }
  } else {
    milestones.forEach(ms => {
      const w = ms.weight || 0;
      if (w === 0) return;
      phaseContribution += w * (ms.progress || 0) / 100;
    });
  }
  const phaseProgress = Math.min(100, Math.max(0, phaseContribution));
  const pc = pColor(phaseProgress);

  // Assignee workload
  const workloadMap={};
  topTasks.forEach(t=>{
    (t.assignees||[]).forEach(a=>{
      if(!workloadMap[a.id]) workloadMap[a.id]={...a,count:0,hours:0};
      workloadMap[a.id].count++;
      workloadMap[a.id].hours+=t.time_estimate||0;
    });
  });
  const workload=Object.values(workloadMap).sort((a,b)=>b.count-a.count);

  // By status
  const byStatus={};
  topTasks.forEach(t=>{byStatus[t.status]=(byStatus[t.status]||0)+1;});

  // totalBudget/totalLogged from milestones (which have correct server-side values)
  // milestones.budgetHours = SUM(time_budget) from tasks in that list
  // milestones.loggedHours = SUM(timesheet_entries.hours) for tasks in that list
  const totalBudget   = milestones.reduce((s,ms)=>s+(ms.budgetHours||0),0);
  const totalLogged   = milestones.reduce((s,ms)=>s+(ms.loggedHours||0),0);
  const totalEstimated = totalBudget; // alias

  const updatePhaseWeight = async (newWeight) => {
    try {
      const w = parseFloat(newWeight)||0;
      const siblings = siblingPhases.filter(p=>p.id!==phaseId);
      const othersTotal = siblings.reduce((s,p)=>s+(p.weight||0),0);
      if (othersTotal + w > 100 && siblings.length > 0) {
        setWeightModal({
          siblings: siblings.map(p=>({...p, task_weight: p.weight||0})),
          newWeight: w,
          newTaskName: phase?.name || 'this phase',
          onConfirm: async (adjusted) => {
            setWeightModal(null);
            for (const s of adjusted) {
              if (s.newWeight !== (s.weight||0)) {
                await api.put(`/phases/${s.id}`, { weight: s.newWeight }).catch(()=>{});
                setSiblingPhases(prev=>prev.map(p=>p.id===s.id?{...p,weight:s.newWeight}:p));
              }
            }
            await api.put(`/phases/${phaseId}`, { weight: w });
            setPhase(prev=>({...prev, weight:w}));
            setSiblingPhases(prev=>prev.map(p=>p.id===phaseId?{...p,weight:w}:p));
          }
        });
        return;
      }
      await api.put(`/phases/${phaseId}`, { weight: w });
      setPhase(prev=>({...prev, weight:w}));
      setSiblingPhases(prev=>prev.map(p=>p.id===phaseId?{...p,weight:w}:p));
    } catch(e) { console.error(e); }
  };

  // ── Weight Distribution Modal (reused from ListPage pattern)
  function WeightDistModal() {
    if (!weightModal) return null;
    const { siblings, newWeight, newTaskName, onConfirm } = weightModal;
    const currentTotal = siblings.reduce((s,t)=>s+(t.task_weight||0),0);
    const excess = currentTotal + newWeight - 100;
    const [adjs, setAdjs] = React.useState(siblings.map(s=>({...s, newWeight:s.task_weight||0})));
    const adjTotal = adjs.reduce((s,t)=>s+(t.newWeight||0),0) + newWeight;
    const isValid = adjTotal <= 100;
    return (
      <div className="modal-overlay">
        <div className="modal" style={{width:'480px',maxHeight:'80vh',display:'flex',flexDirection:'column'}}>
          <div style={{padding:'20px 24px',borderBottom:'1px solid var(--border)'}}>
            <div style={{fontSize:'16px',fontWeight:'800',color:'var(--text)',marginBottom:'4px'}}>⚖️ Adjust Phase Weights</div>
            <div style={{fontSize:'12px',color:'var(--muted)'}}>Adding <strong>{newTaskName}</strong> at <strong>{newWeight}%</strong> exceeds 100%. Reduce other phases below.</div>
          </div>
          <div style={{overflowY:'auto',flex:1,padding:'16px 24px'}}>
            <div style={{fontSize:'11px',fontWeight:'700',color:'var(--muted2)',textTransform:'uppercase',letterSpacing:'.6px',marginBottom:'10px'}}>
              Other Phases — adjust their weights:
            </div>
            {adjs.map((s,i)=>{
              const isReadonly = !!s.readonly;
              return (
              <div key={s.id} style={{display:'flex',alignItems:'center',gap:'10px',padding:'8px 0',borderBottom:'1px solid var(--border)',opacity:isReadonly?0.55:1}}>
                <span style={{flex:1,fontSize:'13px',color:isReadonly?'var(--muted)':'var(--text)',fontWeight:'600'}}>{s.name}{isReadonly&&<span style={{fontSize:'10px',color:'var(--muted)',marginLeft:'6px'}}>🔒 restricted</span>}</span>
                <span style={{fontSize:'11px',color:'var(--muted)',minWidth:'50px',textAlign:'right'}}>was {s.task_weight||0}%</span>
                {isReadonly
                  ? <span style={{width:'58px',height:'30px',border:'1.5px solid var(--border)',borderRadius:'6px',textAlign:'center',fontSize:'13px',fontWeight:'700',background:'var(--surface2)',color:'var(--muted)',display:'flex',alignItems:'center',justifyContent:'center'}}>{s.newWeight}%</span>
                  : <input type="number" min="0" max="100" value={s.newWeight}
                      onChange={e=>setAdjs(prev=>prev.map((a,j)=>j===i?{...a,newWeight:Math.max(0,Math.min(100,parseInt(e.target.value)||0))}:a))}
                      style={{width:'58px',height:'30px',border:`1.5px solid ${isValid?'var(--border)':'#DC2626'}`,borderRadius:'6px',textAlign:'center',fontSize:'13px',fontWeight:'700',background:'var(--surface)',color:'var(--text)',outline:'none'}}/>
                }
                <span style={{fontSize:'11px',color:'var(--muted)'}}>%</span>
              </div>
              );
            })}
          </div>
          <div style={{padding:'14px 24px',borderTop:'1px solid var(--border)',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <span style={{fontSize:'12px',fontWeight:'700',color:isValid?'#16A34A':'#DC2626'}}>
              Total after confirm: {adjTotal}%{isValid?' ✓':' — reduce more'}
            </span>
            <div style={{display:'flex',gap:'8px'}}>
              <button className="btn" onClick={()=>setWeightModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={()=>onConfirm(adjs)} disabled={!isValid}>Confirm & Save</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{flex:1,overflowY:'auto',background:'var(--bg)'}}>
      <WeightDistModal/>

      {/* List Weight Adjust Modal — same as DashboardPage */}
      {listWeightModal && (
        <ListWeightAdjModal
          newItemName={listWeightModal.newItemName}
          siblings={listWeightModal.siblings}
          usedWeight={listWeightModal.usedWeight}
          remaining={listWeightModal.remaining}
          onConfirm={listWeightModal.onConfirm}
          onCancel={listWeightModal.onCancel}
        />
      )}

      {/* Header */}
      <div style={{background:'var(--surface)',borderBottom:'1px solid var(--border)',padding:'18px 28px'}}>
        <div style={{display:'flex',alignItems:'center',gap:'12px'}}>
          <button onClick={()=>navigate(phase?.project_id?`/spaces/${phase.project_id}`:'/spaces')} style={{order:0,background:'none',border:'1px solid var(--border)',borderRadius:'var(--radius)',padding:'5px 10px',cursor:'pointer',color:'var(--muted)',fontSize:'12px',display:'flex',alignItems:'center',gap:'5px'}}>← Dashboard</button>

          <button data-role="export-pdf" onClick={()=>{
            const name=phaseNameVal||phase?.name||'Phase';
            const date=new Date().toLocaleDateString('en-GB',{day:'2-digit',month:'long',year:'numeric'});
            const esc=s=>String(s||'').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
            const STATUS_LABEL={not_started:'Not Started',in_progress:'In Progress',completed:'Completed',on_hold:'On Hold',cancelled:'Cancelled',blocked:'Blocked',at_risk:'At Risk',review:'In Review'};
            const STATUS_COLOR={not_started:'#8A93A6',in_progress:'#5B5BD6',completed:'#16A34A',on_hold:'#D97706',cancelled:'#9CA3AF',blocked:'#DC2626',at_risk:'#F76B15',review:'#0891B2'};
            const PRI_COLOR={urgent:'#DC2626',high:'#F76B15',normal:'#5B5BD6',low:'#16A34A'};

            // Arc helper (upward ∩ gauge)
            // Upward ∩ gauge: sweep=1 (CW going up), large-arc=0 always
            const arc=(pct,col,cx=80,cy=80,r=54,sw=13)=>{
              if(pct<=0) return '';
              const a=Math.PI*Math.min(pct/100,0.9999);
              return `<path d="M ${cx-r},${cy} A ${r},${r} 0 0 1 ${(cx-r*Math.cos(a)).toFixed(2)},${(cy-r*Math.sin(a)).toFixed(2)}" fill="none" stroke="${col}" stroke-width="${sw}" stroke-linecap="round"/>`;
            };
            const arcBg=(cx=80,cy=80,r=54,sw=13)=>`<path d="M ${cx-r},${cy} A ${r},${r} 0 0 1 ${cx+r},${cy}" fill="none" stroke="#e8eaf0" stroke-width="${sw}" stroke-linecap="round"/>`;

            const pp=Math.round(phaseProgress);
            const pc2=pp>=100?'#16A34A':pp>50?'#5B5BD6':pp>20?'#D97706':'#DC2626';
            const budPct=totalBudget>0?Math.min(100,Math.round((totalLogged/totalBudget)*100)):0;
            const budCol=budPct>90?'#DC2626':budPct>70?'#D97706':'#5B5BD6';
            const overrun=totalLogged>totalBudget?(totalLogged-totalBudget).toFixed(1)+'h':null;

            // By status
            const statusRows=Object.entries(byStatus).map(([s,n])=>`<tr>
              <td><span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${STATUS_COLOR[s]||'#8A93A6'};margin-right:6px;vertical-align:middle"></span>${esc(STATUS_LABEL[s]||s)}</td>
              <td style="text-align:center;font-weight:700;color:${STATUS_COLOR[s]||'#333'}">${n}</td>
              <td style="text-align:center;color:#666">${totalTasks>0?Math.round((n/totalTasks)*100):0}%</td></tr>`).join('');

            // Priority breakdown
            const priMap={};topTasks.forEach(t=>{priMap[t.priority||'normal']=(priMap[t.priority||'normal']||0)+1;});
            const priRows=Object.entries(priMap).sort((a,b)=>b[1]-a[1]).map(([p,n])=>`<tr>
              <td><span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${PRI_COLOR[p]||'#8A93A6'};margin-right:6px;vertical-align:middle"></span>${esc(p.charAt(0).toUpperCase()+p.slice(1))}</td>
              <td style="text-align:center;font-weight:700">${n}</td>
              <td style="text-align:center;color:#666">${totalTasks>0?Math.round((n/totalTasks)*100):0}%</td></tr>`).join('');

            // Workload
            const wlRows=workload.map(w=>`<tr>
              <td>${esc(w.full_name)}</td>
              <td style="text-align:center;font-weight:700;color:#5B5BD6">${w.count}</td>
              <td style="text-align:center;color:#666">${(w.hours||0).toFixed(1)}h</td></tr>`).join('');

            // Milestones/Lists
            const msRows=milestones.map(ms=>{
              const msTasks=allTasks.filter(t=>t.milestone_id===ms.id&&!t.parent_task_id);
              const done=msTasks.filter(t=>t.status==='completed').length;
              // Use server-calculated progress — matches web Phase Dashboard
              const mpct=Math.round(ms.progress||0);
              const col=mpct>=100?'#16A34A':mpct>0?'#5B5BD6':'#e8eaf0';
              const bgt=msTasks.reduce((s,t)=>s+(t.time_budget||0),0);
              const lgt=msTasks.reduce((s,t)=>s+(t.time_estimate||0),0);
              return `<tr>
                <td style="font-weight:600">${esc(ms.name)}</td>
                <td style="text-align:center">${msTasks.length}</td>
                <td style="text-align:center;color:#16A34A;font-weight:700">${done}</td>
                <td style="min-width:90px"><div style="height:7px;background:#e8eaf0;border-radius:4px"><div style="height:100%;width:${mpct}%;background:${col};border-radius:4px;min-width:${mpct>0?2:0}px"></div></div></td>
                <td style="text-align:right;font-weight:700;color:${col}">${mpct}%</td>
                <td style="text-align:center;color:#888">${bgt>0?bgt.toFixed(1)+'h':'—'}</td>
                <td style="text-align:center;color:#5B5BD6">${lgt>0?lgt.toFixed(1)+'h':'—'}</td></tr>`;
            }).join('');

            const pw=window.open('','_blank','width=1400,height=900');
            if(!pw)return;
            pw.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8">
<title>${esc(name)} — Phase Dashboard</title>
<style>
*{box-sizing:border-box;margin:0;padding:0}
body{font-family:Arial,Helvetica,sans-serif;background:#f4f6fb;color:#1a1a2e;font-size:12px}
.page{max-width:1200px;margin:0 auto;padding:24px}
.hdr{display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:18px;padding-bottom:12px;border-bottom:2.5px solid #5B5BD6}
.hdr h1{font-size:20px;font-weight:900;color:#1a1a2e}
.hdr p{font-size:11px;color:#888;margin-top:2px}
.brand{font-size:9px;color:#5B5BD6;font-weight:800;letter-spacing:1.2px;text-transform:uppercase;text-align:right;line-height:1.8}
.kpis{display:grid;grid-template-columns:repeat(6,1fr);gap:8px;margin-bottom:14px}
.kpi{background:#fff;border-radius:9px;padding:10px 12px;border:1px solid #e8eaf0;text-align:center}
.kpi .n{font-size:24px;font-weight:900;line-height:1}
.kpi .l{font-size:8px;color:#888;font-weight:700;text-transform:uppercase;letter-spacing:.7px;margin-top:3px}
.r3{display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px;margin-bottom:12px}
.r2{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:12px}
.card{background:#fff;border-radius:9px;border:1px solid #e8eaf0;padding:14px}
.card h2{font-size:8px;font-weight:800;text-transform:uppercase;letter-spacing:1px;color:#888;margin-bottom:8px;padding-bottom:6px;border-bottom:1px solid #f0f0f4}
table{width:100%;border-collapse:collapse}
th{font-size:8px;font-weight:700;text-transform:uppercase;letter-spacing:.5px;color:#aaa;padding:4px 8px;border-bottom:1px solid #f0f0f4;text-align:left}
td{border-bottom:1px solid #f8f9fc;font-size:11px;vertical-align:middle;padding:4px 8px}
tr:last-child td{border-bottom:none}
.gc{display:flex;flex-direction:column;align-items:center;padding:4px 0}
.s4{display:grid;grid-template-columns:1fr 1fr;gap:6px;margin-top:8px}
.sb{background:#f8f9fc;border-radius:6px;padding:6px 10px;border:1px solid #f0f0f4}
.sb .sl{font-size:8px;color:#888;font-weight:700;text-transform:uppercase;letter-spacing:.4px}
.sb .sv{font-size:14px;font-weight:900;margin-top:1px}
.ftr{margin-top:16px;padding-top:10px;border-top:1px solid #e8eaf0;display:flex;justify-content:space-between;font-size:9px;color:#aaa}
@media print{
  .no-print{display:none!important}
  body{background:#fff;-webkit-print-color-adjust:exact;print-color-adjust:exact}
  @page{size:A3 landscape;margin:8mm}
  .page{padding:8px}
}
</style></head><body>
<div class="page">
<div class="hdr">
  <div>
    <div style="font-size:10px;color:#5B5BD6;font-weight:700;text-transform:uppercase;letter-spacing:.8px;margin-bottom:4px">Phase Dashboard</div>
    <h1>📁 ${esc(name)}</h1>
    <p>${milestones.length} list${milestones.length!==1?'s':''} &nbsp;·&nbsp; ${topTasks.length} tasks &nbsp;·&nbsp; Exported: ${date}</p>
  </div>
  <div class="brand">EOMP<br>Space Communication Technologies</div>
</div>
<button class="no-print" onclick="window.print()" style="position:fixed;top:12px;right:12px;padding:9px 22px;background:#5B5BD6;color:#fff;border:none;border-radius:8px;cursor:pointer;font-size:13px;font-weight:700;z-index:9999;box-shadow:0 4px 14px rgba(91,91,214,0.4)">🖨 Print / Save as PDF</button>

<!-- KPIs -->
<div class="kpis">
  <div class="kpi"><div class="n" style="color:#5B5BD6">${totalTasks}</div><div class="l">Total Tasks</div></div>
  <div class="kpi"><div class="n" style="color:#16A34A">${completedTasks}</div><div class="l">Completed</div></div>
  <div class="kpi"><div class="n" style="color:#5B5BD6">${inProgressTasks}</div><div class="l">In Progress</div></div>
  <div class="kpi"><div class="n" style="color:#F76B15">${atRiskTasks}</div><div class="l">At Risk</div></div>
  <div class="kpi"><div class="n" style="color:#DC2626">${overdueTasks}</div><div class="l">Overdue</div></div>
  <div class="kpi"><div class="n" style="color:#0891B2">${pp}%</div><div class="l">Phase Progress</div></div>
</div>

<!-- Row 1: Phase Progress + Hours + Status -->
<div class="r3">
  <div class="card">
    <h2>Phase Progress</h2>
    <div class="gc">
      <svg width="160" height="92" viewBox="0 0 160 90">
        ${arcBg()}
        ${arc(pp,pc2)}
        <text x="80" y="68" text-anchor="middle" font-size="22" font-weight="900" fill="${pc2}">${pp}%</text>
        <text x="80" y="84" text-anchor="middle" font-size="9" fill="#888">Phase Completion</text>
      </svg>
    </div>
    <div class="s4">
      <div class="sb"><div class="sl">Completed</div><div class="sv" style="color:#16A34A">${completedTasks}</div></div>
      <div class="sb"><div class="sl">Remaining</div><div class="sv" style="color:#5B5BD6">${totalTasks-completedTasks}</div></div>
      <div class="sb"><div class="sl">In Progress</div><div class="sv" style="color:#5B5BD6">${inProgressTasks}</div></div>
      <div class="sb"><div class="sl">At Risk</div><div class="sv" style="color:#F76B15">${atRiskTasks}</div></div>
    </div>
  </div>

  <div class="card">
    <h2>Time Budget</h2>
    <div class="gc">
      <svg width="160" height="92" viewBox="0 0 160 90">
        ${arcBg()}
        ${arc(budPct,budCol)}
        <text x="80" y="68" text-anchor="middle" font-size="22" font-weight="900" fill="${budCol}">${budPct}%</text>
        <text x="80" y="84" text-anchor="middle" font-size="9" fill="#888">of budget used</text>
      </svg>
    </div>
    <div class="s4">
      <div class="sb"><div class="sl">Budgeted</div><div class="sv" style="color:#888">${totalBudget.toFixed(1)}h</div></div>
      <div class="sb"><div class="sl">Logged</div><div class="sv" style="color:${budCol}">${totalLogged.toFixed(1)}h</div></div>
      <div class="sb"><div class="sl">Remaining</div><div class="sv" style="color:#16A34A">${Math.max(0,totalBudget-totalLogged).toFixed(1)}h</div></div>
      <div class="sb" style="background:${overrun?'#fef2f2':'#f0fdf4'};border-color:${overrun?'#fecaca':'#bbf7d0'}">
        <div class="sl" style="color:${overrun?'#DC2626':'#16A34A'}">${overrun?'Overrun':'On Track'}</div>
        <div class="sv" style="color:${overrun?'#DC2626':'#16A34A'}">${overrun?'+'+overrun:'✓ '+budPct+'%'}</div>
      </div>
    </div>
  </div>

  <div class="card">
    <h2>By Status</h2>
    <table><thead><tr><th>Status</th><th style="text-align:center">Count</th><th style="text-align:center">%</th></tr></thead>
    <tbody>${statusRows||'<tr><td colspan="3" style="color:#aaa;padding:8px;text-align:center">No data</td></tr>'}</tbody></table>
    <div style="margin-top:10px;padding-top:8px;border-top:1px solid #f0f0f4">
      <h2 style="margin-bottom:6px">Priority Breakdown</h2>
      <table><thead><tr><th>Priority</th><th style="text-align:center">Tasks</th><th style="text-align:center">%</th></tr></thead>
      <tbody>${priRows||'<tr><td colspan="3" style="color:#aaa;padding:8px;text-align:center">No data</td></tr>'}</tbody></table>
    </div>
  </div>
</div>

<!-- Row 2: Workload + (empty for spacing) -->
<div class="r2">
  <div class="card">
    <h2>Team Workload</h2>
    <table><thead><tr><th>Member</th><th style="text-align:center">Tasks</th><th style="text-align:center">Hours</th></tr></thead>
    <tbody>${wlRows||'<tr><td colspan="3" style="color:#aaa;padding:8px;text-align:center">No data</td></tr>'}</tbody></table>
  </div>
  <div class="card">
    <h2>Phase Info</h2>
    <table>
      <tr><td style="color:#888;padding:5px 8px">Phase Name</td><td style="padding:5px 8px;font-weight:700">${esc(name)}</td></tr>
      <tr><td style="color:#888;padding:5px 8px">Total Lists</td><td style="padding:5px 8px;font-weight:700">${milestones.length}</td></tr>
      <tr><td style="color:#888;padding:5px 8px">Total Tasks</td><td style="padding:5px 8px;font-weight:700">${totalTasks}</td></tr>
      <tr><td style="color:#888;padding:5px 8px">Completed</td><td style="padding:5px 8px;font-weight:700;color:#16A34A">${completedTasks} (${totalTasks>0?Math.round(completedTasks/totalTasks*100):0}%)</td></tr>
      <tr><td style="color:#888;padding:5px 8px">Budget Hours</td><td style="padding:5px 8px;font-weight:700">${totalBudget.toFixed(1)}h</td></tr>
      <tr><td style="color:#888;padding:5px 8px">Logged Hours</td><td style="padding:5px 8px;font-weight:700;color:#5B5BD6">${totalLogged.toFixed(1)}h</td></tr>
      <tr><td style="color:#888;padding:5px 8px">Overdue</td><td style="padding:5px 8px;font-weight:700;color:${overdueTasks>0?'#DC2626':'#16A34A'}">${overdueTasks}</td></tr>
    </table>
  </div>
</div>

<!-- Lists/Milestones -->
<div class="card" style="margin-bottom:12px">
  <h2>Lists Progress</h2>
  <table><thead><tr><th>List</th><th style="text-align:center">Tasks</th><th style="text-align:center">Done</th><th style="min-width:100px">Progress</th><th style="text-align:right">%</th><th style="text-align:center">Budget</th><th style="text-align:center">Logged</th></tr></thead>
  <tbody>${msRows||'<tr><td colspan="7" style="color:#aaa;padding:8px;text-align:center">No data</td></tr>'}</tbody></table>
</div>

<div class="ftr">
  <span>Generated by Enterprise Operations Management Platform (EOMP) &nbsp;·&nbsp; Space Communication Technologies</span>
  <span>${date}</span>
</div>
</div></body></html>`);
            pw.document.close();         }} style={{order:4,background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:'var(--radius)',padding:'5px 12px',cursor:'pointer',color:'var(--text)',fontSize:'12px',fontWeight:'600',display:'flex',alignItems:'center',gap:'5px'}}>🖨️ Export PDF</button>
          <div style={{order:1,width:36,height:36,borderRadius:'10px',background:'rgba(217,119,6,0.12)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'18px'}}>📁</div>
          <div style={{order:2}}>
            {editingPhaseName
              ? <input value={phaseNameVal} onChange={e=>setPhaseNameVal(e.target.value)} autoFocus
                  onBlur={savePhaseNameFn} onKeyDown={e=>{if(e.key==='Enter')savePhaseNameFn();if(e.key==='Escape')setEditingPhaseName(false);}}
                  style={{fontSize:'18px',fontWeight:'800',border:'none',borderBottom:'2px solid var(--brand)',outline:'none',background:'transparent',color:'var(--text)',width:'260px'}}/>
              : <h1 onDoubleClick={()=>setEditingPhaseName(true)} title="Double-click to rename"
                  style={{fontSize:'18px',fontWeight:'800',color:'var(--text)',margin:0,cursor:'text'}}>{phase?.name||'Phase Dashboard'} <span style={{fontSize:'11px',color:'var(--muted)',fontWeight:'400'}}>✏</span></h1>
            }
            <p style={{fontSize:'12px',color:'var(--muted)',margin:0}}>{milestones.length} lists · {totalTasks} tasks</p>
          </div>
          {/* Phase weight — editable */}
          <div style={{order:3,display:'flex',alignItems:'center',gap:'8px',padding:'8px 14px',background:'var(--surface2)',borderRadius:'var(--radius-lg)',border:'1px solid var(--border)'}}>
            <div>
              <div style={{fontSize:'10px',color:'var(--muted)',fontWeight:'700',textTransform:'uppercase',letterSpacing:'.6px',marginBottom:'3px'}}>Phase Weight</div>
              <div style={{display:'flex',alignItems:'center',gap:'6px'}}>
                <input type="number" min="0" max="100" defaultValue={phase?.weight||0}
                  onBlur={e=>updatePhaseWeight(e.target.value)}
                  onKeyDown={e=>{if(e.key==='Enter')e.target.blur();}}
                  style={{width:'54px',height:'30px',border:'1.5px solid var(--brand)',borderRadius:'6px',background:'var(--surface)',color:'var(--brand)',textAlign:'center',fontSize:'15px',fontWeight:'800',outline:'none',padding:'0 4px'}}/>
                <span style={{fontSize:'13px',color:'var(--brand)',fontWeight:'700'}}>%</span>
              </div>
            </div>
          </div>
          {/* Phase progress */}
          <div style={{order:6,marginLeft:'auto',display:'flex',alignItems:'center',gap:'10px'}}>
            <div style={{textAlign:'right'}}>
              <div style={{fontSize:'11px',color:'var(--muted)',fontWeight:'600',textTransform:'uppercase',letterSpacing:'.6px'}}>Phase Progress</div>
              <div style={{fontSize:'22px',fontWeight:'800',color:pc,letterSpacing:'-1px'}}>{Math.round(phaseProgress)}%</div>
            </div>
            <div style={{width:56,height:56,position:'relative'}}>
              <svg width={56} height={56} style={{transform:'rotate(-90deg)'}}>
                <circle cx={28} cy={28} r={22} fill="none" stroke="#EEF0F5" strokeWidth="5"/>
                <circle cx={28} cy={28} r={22} fill="none" stroke={pc} strokeWidth="5"
                  strokeDasharray={2*Math.PI*22} strokeDashoffset={2*Math.PI*22*(1-phaseProgress/100)} strokeLinecap="round"/>
              </svg>
            </div>
          </div>

        {/* ── Add List button + inline form (in header row, between weight and progress) ── */}
        <div style={{order:5,display:'flex',alignItems:'center',gap:'10px'}}>
          {!showNewList
            ? <button onClick={()=>setShowNewList(true)}
                className="btn btn-primary"
                style={{display:'flex',alignItems:'center',gap:'6px',fontSize:'12px',fontWeight:'700',padding:'7px 14px'}}>
                <span style={{fontSize:'15px',lineHeight:1}}>＋</span> Add List
              </button>
            : <div style={{display:'flex',alignItems:'center',gap:'8px',background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:'10px',padding:'8px 12px',flexWrap:'wrap'}}>
                <input
                  autoFocus
                  value={newListName}
                  onChange={e=>setNewListName(e.target.value)}
                  onKeyDown={e=>{if(e.key==='Enter')createList();if(e.key==='Escape'){setShowNewList(false);setNewListName('');}}}
                  placeholder="List name…"
                  style={{border:'1px solid var(--border)',borderRadius:'6px',padding:'5px 10px',fontSize:'13px',background:'var(--surface)',color:'var(--text)',width:'180px'}}
                />
                <input
                  type="color"
                  value={newListColor}
                  onChange={e=>setNewListColor(e.target.value)}
                  style={{width:'32px',height:'32px',border:'1.5px solid var(--border)',borderRadius:'6px',cursor:'pointer',padding:'2px',background:'var(--surface)'}}
                  title="List colour"
                />
                <button onClick={createList} disabled={addingList||!newListName.trim()}
                  className="btn btn-primary"
                  style={{fontSize:'12px',fontWeight:'700',padding:'6px 14px',opacity:(!newListName.trim()||addingList)?0.5:1}}>
                  {addingList?'Creating…':'Create'}
                </button>
                <button onClick={()=>{setShowNewList(false);setNewListName('');}}
                  className="btn"
                  style={{fontSize:'12px',padding:'6px 12px'}}>
                  Cancel
                </button>
              </div>
          }
        </div>
      </div>
      </div>

      <div style={{padding:'24px 28px',display:'flex',flexDirection:'column',gap:'16px'}}>

        {/* KPI Cards */}
        <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:'12px'}}>
          {[
            {label:'Total Tasks',value:totalTasks,sub:`${completionRate}% done`,icon:'📋',color:'var(--text)'},
            {label:'Completed',value:completedTasks,sub:'finished',icon:'✅',color:'#16A34A'},
            {label:'In Progress',value:inProgressTasks,sub:'active',icon:'⚡',color:'#5B5BD6'},
            {label:'At Risk',value:atRiskTasks,sub:atRiskTasks>0?'needs attention':'ok',icon:'⚠️',color:'#D97706'},
            {label:'Overdue',value:overdueTasks,sub:overdueTasks>0?'action needed':'on track',icon:'🔴',color:overdueTasks>0?'#DC2626':'#16A34A'},
          ].map((c,i)=>(
            <div key={i} className="card" style={{padding:'16px 18px'}}>
              <div style={{display:'flex',justifyContent:'space-between',marginBottom:'8px'}}>
                <span style={{fontSize:'10px',fontWeight:'700',textTransform:'uppercase',letterSpacing:'.6px',color:'var(--muted)'}}>{c.label}</span>
                <span style={{fontSize:'14px'}}>{c.icon}</span>
              </div>
              <div style={{fontSize:'28px',fontWeight:'800',color:c.color,letterSpacing:'-1px'}}>{c.value}</div>
              <div style={{fontSize:'11px',color:'var(--muted)',marginTop:'4px'}}>{c.sub}</div>
            </div>
          ))}
        </div>

        {/* Charts row */}
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'14px'}}>
          {/* By Status */}
          <div className="card" style={{padding:'18px'}}>
            <div style={{fontSize:'11px',fontWeight:'700',textTransform:'uppercase',letterSpacing:'.7px',color:'var(--muted2)',marginBottom:'14px'}}>By Status</div>
            {Object.entries(byStatus).map(([s,count])=>{
              const c=STATUS_COLORS[s]||'#8A93A6';
              const pct=totalTasks>0?(count/totalTasks)*100:0;
              return (
                <div key={s} style={{display:'flex',alignItems:'center',gap:'8px',marginBottom:'8px'}}>
                  <span style={{fontSize:'11px',fontWeight:'600',color:c,width:'90px',flexShrink:0}}>{STATUS_LABELS[s]||s}</span>
                  <div style={{flex:1,height:7,background:'var(--surface3)',borderRadius:'99px',overflow:'hidden'}}>
                    <div style={{height:'100%',width:`${pct}%`,background:c,borderRadius:'99px'}}/>
                  </div>
                  <span style={{fontSize:'11px',color:'var(--muted)',width:'20px',textAlign:'right',flexShrink:0,fontWeight:'600'}}>{count}</span>
                </div>
              );
            })}
          </div>

          {/* Time Budget — gauge arc */}
          <div className="card" style={{padding:'18px'}}>
            <div style={{fontSize:'11px',fontWeight:'700',textTransform:'uppercase',letterSpacing:'.7px',color:'var(--muted2)',marginBottom:'10px'}}>Time Budget</div>
            {totalBudget>0?(()=>{
              const pct = Math.min(100,(totalLogged/totalBudget)*100);
              const r=52,cx=70,cy=65,sw=11;
              const circ=2*Math.PI*r;
              const dashArr=circ*(180/360); // half circle
              const dashOff=dashArr*(1-pct/100);
              const col=pct>90?'#DC2626':pct>70?'#D97706':'#5B5BD6';
              return (
                <div style={{display:'flex',flexDirection:'column',alignItems:'center'}}>
                  <div style={{position:'relative',width:140,height:80}}>
                    <svg width={140} height={80} viewBox="0 0 140 80">
                      <path d={`M ${cx-r},${cy} A ${r},${r} 0 0 1 ${cx+r},${cy}`} fill="none" stroke="var(--surface3)" strokeWidth={sw} strokeLinecap="round"/>
                      <path d={`M ${cx-r},${cy} A ${r},${r} 0 0 1 ${cx+r},${cy}`} fill="none" stroke={col} strokeWidth={sw} strokeLinecap="round"
                        strokeDasharray={dashArr} strokeDashoffset={dashArr*(1-pct/100)}
                        style={{transition:'stroke-dashoffset .6s ease'}}/>
                    </svg>
                    <div style={{position:'absolute',bottom:0,left:0,right:0,textAlign:'center'}}>
                      <div style={{fontSize:'20px',fontWeight:'900',color:col}}>{Math.round(pct)}%</div>
                      <div style={{fontSize:'10px',color:'var(--muted)',fontWeight:'600'}}>of budget used</div>
                    </div>
                  </div>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'8px',width:'100%',marginTop:'10px'}}>
                    {[
                      {l:'Budgeted',v:`${totalBudget.toFixed(1)}h`,c:'var(--muted2)'},
                      {l:'Logged',v:`${totalLogged.toFixed(1)}h`,c:col},
                      {l:'Remaining',v:`${Math.max(0,totalBudget-totalLogged).toFixed(1)}h`,c:'#16A34A'},
                      {l:'Overrun',v:totalLogged>totalBudget?`+${(totalLogged-totalBudget).toFixed(1)}h`:'—',c:totalLogged>totalBudget?'#DC2626':'var(--muted)'},
                    ].map((s,i)=>(
                      <div key={i} style={{background:'var(--surface2)',borderRadius:'var(--radius)',padding:'7px 10px'}}>
                        <div style={{fontSize:'10px',color:'var(--muted)',fontWeight:'600'}}>{s.l}</div>
                        <div style={{fontSize:'14px',fontWeight:'800',color:s.c}}>{s.v}</div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })() : <div style={{color:'var(--muted)',fontSize:'12px',textAlign:'center',padding:'20px 0'}}>No time budget set</div>}
          </div>

          {/* Team Workload — fixed-height card, scrollable legend */}
          <div className="card" style={{padding:'18px',display:'flex',flexDirection:'column',height:'320px'}}>
            <div style={{fontSize:'11px',fontWeight:'700',textTransform:'uppercase',letterSpacing:'.7px',color:'var(--muted2)',marginBottom:'10px',flexShrink:0}}>Team Workload</div>
            {workload.length===0
              ? <div style={{color:'var(--muted)',fontSize:'12px',textAlign:'center',padding:'20px 0'}}>No assigned tasks</div>
              : (()=>{
                  const SLICE_COLORS=['#5B5BD6','#30A46C','#F76B15','#E54D2E','#6E56CF','#0090FF','#D6409F','#FFC53D'];
                  const total=workload.reduce((s,w)=>s+w.count,0)||1;
                  const cx=70,cy=60,r=48,sw=20;
                  let offset=0;
                  const circ=2*Math.PI*r;
                  return (
                    <>
                      {/* Fixed donut chart */}
                      <div style={{display:'flex',justifyContent:'center',flexShrink:0}}>
                        <svg width={140} height={124} viewBox="0 0 140 124">
                          {workload.map((w,i)=>{
                            const pct=w.count/total;
                            const dash=circ*pct-1;
                            const gap=circ-dash;
                            offset+=pct;
                            const col=w.avatar_color||SLICE_COLORS[i%SLICE_COLORS.length];
                            return <circle key={w.id} cx={cx} cy={cy} r={r} fill="none"
                              stroke={col} strokeWidth={sw}
                              strokeDasharray={`${Math.max(0,dash)} ${gap+1}`}
                              strokeDashoffset={circ*(1-(offset-pct))-circ/4}
                              style={{transition:'all .4s'}}/>;
                          })}
                          <circle cx={cx} cy={cy} r={r-sw/2-1} fill="#fff" />
                          <text x={cx} y={cy-5} textAnchor="middle" fontSize="20" fontWeight="800" fill="#1a1a2e">{total}</text>
                          <text x={cx} y={cy+13} textAnchor="middle" fontSize="9" fill="#888">tasks total</text>
                        </svg>
                      </div>
                      {/* Scrollable legend — fixed height, never expands card */}
                      <div style={{flex:1,overflowY:'auto',marginTop:'8px',minHeight:0}}>
                        {workload.map((w,i)=>{
                          const col=w.avatar_color||SLICE_COLORS[i%SLICE_COLORS.length];
                          return (
                            <div key={w.id} style={{display:'flex',alignItems:'center',gap:'7px',padding:'3px 2px',borderBottom:'1px solid var(--border)'}}>
                              <div style={{width:9,height:9,borderRadius:'50%',background:col,flexShrink:0}}/>
                              <span style={{fontSize:'11px',color:'var(--text)',flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{w.full_name}</span>
                              <span style={{fontSize:'10px',color:'var(--muted)',fontWeight:'700',flexShrink:0,whiteSpace:'nowrap'}}>{w.count}t · {w.hours.toFixed(0)}h</span>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  );
                })()
            }
          </div>
        </div>

        {/* Milestones table */}
        <div className="card" style={{overflow:'hidden'}}>
          <div style={{padding:'16px 20px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
            <div style={{fontSize:'12px',fontWeight:'700',textTransform:'uppercase',letterSpacing:'.7px',color:'var(--muted2)'}}>Milestones</div>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'2fr 80px 80px 80px 1fr 80px',background:'var(--surface2)',borderBottom:'1px solid var(--border)'}}>
            {['Milestone','Tasks','Done','At Risk','Progress',''].map((h,i)=>(
              <div key={i} style={{padding:'8px 14px',fontSize:'10px',fontWeight:'700',textTransform:'uppercase',letterSpacing:'.6px',color:'var(--muted)'}}>{h}</div>
            ))}
          </div>
          {milestones.length===0
            ? <div style={{padding:'32px',textAlign:'center',color:'var(--muted)'}}>No milestones in this phase</div>
            : milestones.map(ms=>{
                const msTasks=allTasks.filter(t=>t.milestone_id===ms.id&&!t.parent_task_id);
                const msDone=msTasks.filter(t=>t.status==='completed').length;
                const msRisk=msTasks.filter(t=>['at_risk','escalated'].includes(t.status)).length;
                // Use server-calculated progress (calculateListProgress) — correct weighted model
                const msProgress=ms.progress||0;
                const mpc=pColor(msProgress);
                return (
                  <div key={ms.id} style={{display:'grid',gridTemplateColumns:'2fr 80px 80px 80px 1fr 80px',borderBottom:'1px solid var(--border)',alignItems:'center',transition:'background .1s'}}
                    onMouseEnter={e=>e.currentTarget.style.background='var(--surface2)'}
                    onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                    <div style={{padding:'12px 14px',display:'flex',alignItems:'center',gap:'8px'}}>
                      <div style={{width:8,height:8,borderRadius:'2px',background:ms.color||'#5B5BD6',flexShrink:0}}/>
                      <span style={{fontSize:'13px',fontWeight:'600',color:'var(--text)'}}>{ms.name}</span>
                    </div>
                    <div style={{padding:'12px 14px',fontSize:'12px',fontWeight:'600',color:'var(--muted2)'}}>{msTasks.length}</div>
                    <div style={{padding:'12px 14px',fontSize:'12px',fontWeight:'600',color:'#16A34A'}}>{msDone}</div>
                    <div style={{padding:'12px 14px',fontSize:'12px',fontWeight:'600',color:msRisk>0?'#DC2626':'var(--muted)'}}>{msRisk||'—'}</div>
                    <div style={{padding:'12px 14px',display:'flex',alignItems:'center',gap:'8px'}}>
                      <div style={{flex:1,height:7,background:'var(--surface3)',borderRadius:'99px',overflow:'hidden'}}>
                        <div style={{height:'100%',width:`${msProgress}%`,background:mpc,borderRadius:'99px',transition:'width .4s'}}/>
                      </div>
                      <span style={{fontSize:'11px',fontWeight:'700',color:mpc,minWidth:'32px',textAlign:'right'}}>{Math.round(msProgress)}%</span>
                    </div>
                    <div style={{padding:'12px 14px'}}>
                      <button className="btn btn-sm" style={{padding:'3px 10px',fontSize:'11px'}} onClick={()=>navigate(`/lists/${ms.id}`)}>View →</button>
                    </div>
                  </div>
                );
              })
          }
        </div>

        {/* All tasks table */}
        <div className="card" style={{overflow:'hidden'}}>
          <div style={{padding:'14px 20px',borderBottom:'1px solid var(--border)'}}>
            <div style={{fontSize:'12px',fontWeight:'700',textTransform:'uppercase',letterSpacing:'.7px',color:'var(--muted2)'}}>All Tasks in Phase ({topTasks.length})</div>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'minmax(180px,2fr) 120px 100px 120px 1fr 70px',background:'var(--surface2)',borderBottom:'1px solid var(--border)'}}>
            {['Task','Milestone','Status','Assignees','Progress','Due'].map((h,i)=>(
              <div key={i} style={{padding:'8px 12px',fontSize:'10px',fontWeight:'700',textTransform:'uppercase',letterSpacing:'.6px',color:'var(--muted)'}}>{h}</div>
            ))}
          </div>
          {topTasks.length===0
            ? <div style={{padding:'32px',textAlign:'center',color:'var(--muted)'}}>No tasks found</div>
            : topTasks.map(t=>{
                const p=t.calculatedProgress??t.progress??0;
                const pc2=pColor(p);
                const sc=STATUS_COLORS[t.status]||'#8A93A6';
                const ms=milestones.find(m=>m.id===t.milestone_id);
                const isOverdue=t.due_date&&t.due_date<new Date().toISOString().slice(0,10)&&t.status!=='completed';
                return (
                  <div key={t.id} style={{display:'grid',gridTemplateColumns:'minmax(180px,2fr) 120px 100px 120px 1fr 70px',borderBottom:'1px solid var(--border)',alignItems:'center',transition:'background .1s'}}
                    onMouseEnter={e=>e.currentTarget.style.background='var(--surface2)'}
                    onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                    <div style={{padding:'10px 12px',fontSize:'13px',fontWeight:'600',color:'var(--text)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{t.name}</div>
                    <div style={{padding:'10px 12px',fontSize:'11px',color:'var(--muted2)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{ms?.name||'—'}</div>
                    <div style={{padding:'10px 12px'}}>
                      <span style={{fontSize:'10px',fontWeight:'600',padding:'2px 7px',borderRadius:'99px',background:`${sc}18`,color:sc,border:`1px solid ${sc}30`,whiteSpace:'nowrap'}}>
                        {STATUS_LABELS[t.status]||t.status}
                      </span>
                    </div>
                    <div style={{padding:'10px 12px',display:'flex',gap:'-4px'}}>
                      {(t.assignees||[]).slice(0,3).map((a,ai)=>(
                        <div key={a.id} title={a.full_name} style={{marginLeft:ai>0?-6:0}}><Avatar name={a.full_name} color={a.avatar_color} size={22}/></div>
                      ))}
                      {(t.assignees||[]).length===0&&<span style={{fontSize:'11px',color:'var(--muted)'}}>—</span>}
                    </div>
                    <div style={{padding:'10px 12px',display:'flex',alignItems:'center',gap:'6px'}}>
                      <div style={{flex:1,height:6,background:'var(--surface3)',borderRadius:'99px',overflow:'hidden'}}>
                        <div style={{height:'100%',width:`${p}%`,background:pc2,borderRadius:'99px'}}/>
                      </div>
                      <span style={{fontSize:'11px',fontWeight:'700',color:pc2,minWidth:'28px',textAlign:'right'}}>{Math.round(p)}%</span>
                    </div>
                    <div style={{padding:'10px 12px',fontSize:'11px',color:isOverdue?'#DC2626':'var(--muted2)',fontWeight:isOverdue?'700':'400'}}>
                      {t.due_date||'—'}
                    </div>
                  </div>
                );
              })
          }
        </div>
      </div>
    {/* Weight error toast */}
    {toast && (
      <div style={{position:'fixed',bottom:'24px',left:'50%',transform:'translateX(-50%)',zIndex:9999,
        background:toast.type==='error'?'#DC2626':'#16A34A',color:'#fff',borderRadius:'12px',
        padding:'14px 24px',boxShadow:'0 8px 32px rgba(0,0,0,0.2)',fontSize:'13px',fontWeight:'600',
        maxWidth:'480px',textAlign:'center',animation:'slideUp .2s ease'}}>
        {toast.msg}
      </div>
    )}
    </div>
  );
}
